import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    EmptyState, SectionTitle,
    getCategoryColor, formatDuration, formatTime,
    API,
} from './analyticsHelpers';

// ─── Full hourly chart ────────────────────────────────────────────────────────
const HourlyChart = ({ data, clicksByHour }) => {
    const sessionMax = Math.max(...(data || []), 1);
    const clickMax = Math.max(...(clicksByHour || Array(24).fill(0)), 1);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const peakSessionHour = data ? data.indexOf(Math.max(...data)) : -1;

    return (
        <div>
            {/* Legend */}
            <div className="chart-legend-row">
                <span className="legend-item">
                    <span className="legend-dot" style={{ background: '#2B8EE8' }} /> Sessions
                </span>
                {clicksByHour && (
                    <span className="legend-item">
                        <span className="legend-dot" style={{ background: '#ec4899' }} /> Clicks
                    </span>
                )}
            </div>
            <div className="hourly-chart full">
                {hours.map(h => {
                    const sessions = data?.[h] || 0;
                    const clicks = clicksByHour?.[h] || 0;
                    const isPeak = h === peakSessionHour && sessions > 0;
                    return (
                        <div key={h} className="hour-col">
                            <div className="hour-bar-wrap dual">
                                {clicksByHour && (
                                    <div
                                        className="hour-bar clicks-bar-h"
                                        style={{
                                            height: `${(clicks / clickMax) * 100}%`,
                                            background: '#ec4899',
                                            opacity: 0.4,
                                        }}
                                        title={`${h}:00 — ${clicks} clicks`}
                                    />
                                )}
                                <div
                                    className="hour-bar"
                                    style={{
                                        height: `${(sessions / sessionMax) * 100}%`,
                                        background: isPeak ? '#f59e0b' : '#2B8EE8',
                                        opacity: isPeak ? 1 : 0.6,
                                    }}
                                    title={`${h}:00 — ${sessions} sessions`}
                                />
                            </div>
                            {h % 4 === 0 && <div className="hour-label">{h}:00</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── Bounce rate card ─────────────────────────────────────────────────────────
// Bounces = sessions < 30s. Computed from raw session data (kpis doesn't expose this,
// but avgDuration and totalSessions let us show a contextual note instead).
const BounceCard = ({ kpis }) => {
    const avgDuration = kpis.avgDurationSeconds || 0;
    // Heuristic: if avg duration < 60s the bounce rate is likely high
    const quality = avgDuration < 30 ? 'high' : avgDuration < 90 ? 'medium' : 'low';
    const qualityLabel = { high: 'High', medium: 'Medium', low: 'Low' }[quality];
    const qualityColor = { high: '#ef4444', medium: '#f59e0b', low: '#22C55E' }[quality];

    return (
        <div className="bounce-card">
            <div className="bounce-header">
                <h4 className="bounce-title">Engagement Quality</h4>
                <span className="bounce-badge" style={{ background: qualityColor + '22', color: qualityColor }}>
                    {qualityLabel} bounce risk
                </span>
            </div>
            <div className="bounce-stats">
                <div className="bounce-stat">
                    <span className="bounce-stat-value" style={{ color: qualityColor }}>
                        {formatDuration(avgDuration)}
                    </span>
                    <span className="bounce-stat-label">Avg session length</span>
                </div>
                <div className="bounce-stat">
                    <span className="bounce-stat-value">{kpis.totalSessions}</span>
                    <span className="bounce-stat-label">Total sessions</span>
                </div>
                <div className="bounce-stat">
                    <span className="bounce-stat-value">{kpis.peakConcurrentSessions}</span>
                    <span className="bounce-stat-label">Peak concurrent</span>
                </div>
            </div>
            <p className="bounce-note">
                Sessions under 30s are classified as bounces.
                {quality === 'high' && ' Consider improving map load speed or onboarding.'}
                {quality === 'medium' && ' Engagement is moderate — check if key markers are easy to find.'}
                {quality === 'low' && ' Attendees are spending meaningful time with your map.'}
            </p>
        </div>
    );
};

// ─── Live click feed ──────────────────────────────────────────────────────────
const LiveClickFeed = ({ clicks, isActive, authHeader, mapId }) => {
    const [liveFeed, setLiveFeed] = useState(null);
    const [feedLoading, setFeedLoading] = useState(false);

    const fetchRecentClicks = useCallback(async () => {
        if (!mapId || !authHeader) return;
        setFeedLoading(true);
        try {
            const from = new Date(Date.now() - 60 * 60 * 1000).toISOString(); // last hour
            const res = await fetch(
                `${API}/api/analytics/map/${mapId}/clicks?from=${from}`,
                { headers: authHeader }
            );
            const data = await res.json();
            if (data.success) {
                const sorted = (data.data.clicks || [])
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .slice(0, 50);
                setLiveFeed(sorted);
            }
        } catch (_) {
            /* silently fail */
        } finally {
            setFeedLoading(false);
        }
    }, [mapId, authHeader]);

    useEffect(() => {
        fetchRecentClicks();
    }, [fetchRecentClicks]);

    useEffect(() => {
        if (!isActive) return;
        const interval = setInterval(fetchRecentClicks, 15000);
        return () => clearInterval(interval);
    }, [isActive, fetchRecentClicks]);

    const feedData = liveFeed || clicks?.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 50) || [];

    return (
        <div className="live-feed-wrap">
            <div className="live-feed-header">
                <h3 className="ad-card-title" style={{ marginBottom: 0 }}>Recent Clicks</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {isActive && <span className="live-badge inline"><span className="live-dot" /> LIVE</span>}
                    <button className="ad-see-all" onClick={fetchRecentClicks} disabled={feedLoading}>
                        {feedLoading ? 'Refreshing…' : '↻ Refresh'}
                    </button>
                </div>
            </div>
            {feedData.length === 0 ? (
                <EmptyState message="No clicks recorded yet" />
            ) : (
                <div className="live-feed">
                    {feedData.map((click, i) => {
                        const name = click.markerId?.name || 'Unknown marker';
                        const cat = click.markerId?.category;
                        const color = getCategoryColor(cat);
                        return (
                            <div key={`${click._id || i}`} className="feed-row">
                                <span
                                    className="feed-cat-dot"
                                    style={{ background: color }}
                                    title={cat}
                                />
                                <span className="feed-marker-name">{name}</span>
                                {cat && (
                                    <span className="feed-cat-label" style={{
                                        background: color + '22',
                                        color,
                                    }}>
                                        {cat.replace('_', ' ')}
                                    </span>
                                )}
                                <span className="feed-time">{formatTime(click.timestamp)}</span>
                                {click.userLat && (
                                    <span className="feed-gps-dot" title="GPS position recorded" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── ActivityTab ──────────────────────────────────────────────────────────────
const ActivityTab = ({ analyticsData, clicks, isActive, authHeader }) => {
    const { map, kpis } = analyticsData;

    // Build clicks-by-hour from raw click data
    const clicksByHour = useMemo(() => {
        if (!clicks || clicks.length === 0) return null;
        const buckets = Array(24).fill(0);
        clicks.forEach(c => {
            const h = new Date(c.timestamp).getHours();
            buckets[h]++;
        });
        return buckets;
    }, [clicks]);

    // Session starts over time — 30-min buckets for a simple sparkline
    const sessionTimeline = useMemo(() => {
        // This comes from kpis.hourlyActivity which is sessions-per-hour
        return kpis.hourlyActivity || Array(24).fill(0);
    }, [kpis.hourlyActivity]);

    const peakHour = sessionTimeline.indexOf(Math.max(...sessionTimeline));
    const totalClicksFromFeed = clicks?.length || 0;

    return (
        <div className="ad-tab-content">

            {/* ── Summary stats row ── */}
            <section className="ad-section">
                <div className="ad-card">
                    <div className="activity-summary-row">
                        <div className="activity-stat">
                            <span className="activity-stat-value">{kpis.totalSessions}</span>
                            <span className="activity-stat-label">Sessions</span>
                        </div>
                        <div className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{totalClicksFromFeed}</span>
                            <span className="activity-stat-label">Marker Clicks</span>
                        </div>
                        <div className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">
                                {kpis.totalSessions > 0
                                    ? (totalClicksFromFeed / kpis.totalSessions).toFixed(1)
                                    : '—'}
                            </span>
                            <span className="activity-stat-label">Clicks / Session</span>
                        </div>
                        <div className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">
                                {peakHour >= 0 && sessionTimeline[peakHour] > 0
                                    ? `${peakHour}:00`
                                    : '—'}
                            </span>
                            <span className="activity-stat-label">Peak Hour</span>
                        </div>
                        <div className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{formatDuration(kpis.avgDurationSeconds)}</span>
                            <span className="activity-stat-label">Avg Duration</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Hourly chart ── */}
            <section className="ad-section">
                <div className="ad-card">
                    <SectionTitle sub="Sessions and marker clicks per hour of day">
                        Activity Timeline
                    </SectionTitle>
                    <HourlyChart data={sessionTimeline} clicksByHour={clicksByHour} />
                </div>
            </section>

            {/* ── Bounce card + live feed ── */}
            <section className="ad-section ad-two-col">
                <div className="ad-card">
                    <BounceCard kpis={kpis} />
                </div>
                <div className="ad-card ad-card-wide">
                    <LiveClickFeed
                        clicks={clicks}
                        isActive={isActive}
                        authHeader={authHeader}
                        mapId={map?.id}
                    />
                </div>
            </section>

        </div>
    );
};

export default ActivityTab;